<template>
  <div class="courses-list__container">
    <h1 class="text-3xl font-normal mb-4">Courses</h1>
    <RichContent
      v-if="pageContent"
      class="main-content"
      :content="pageContent"
    />
    <h2 class="text-xl font-normal mt-8">
      Search Courses
    </h2>
    <BaseAccordion>
      <template #trigger="{ setIsOpen, isOpen }">
        <div class="search-wrapper flex mt-4">
          <SearchInput
            v-model="searchQuery"
            placeholder="Search for courses..."
            :is-loading="isLoading"
            data-test="search-input"
          />
          <BaseButton
            v-if="!hasDepartmentFilter"
            aria-label="Advanced filters"
            aria-expanded="true"
            :type="isOpen ? 'primary' : 'secondary'"
            class="ml-4 bg-theme-navbar-background color-theme-navbar-link hover:opacity-80"
            data-test="advanced-filter-btn"
            @click="setIsOpen(!isOpen)"
          >
            <span class="visually-hidden">Toggle advanced filters</span>
            <BaseIcon icon="filter" />
          </BaseButton>
          <span
            class="visually-hidden ui-font"
            role="status"
          >
            <template v-if="isOpen">Advanced filters open</template>
            <template v-else>Advanced filters collapsed</template>
          </span>
        </div>
      </template>
      <template #content>
        <div class="bg-white mb-4 p-4 -mt-8 ui-font">
          <h4 class="font-semibold pt-2">Advanced Search</h4>
          <DepartmentFilter v-model="departmentFilter" />
        </div>
      </template>
    </BaseAccordion>
    <h2 class="text-lg font-normal flex justify-between">
      Results
      <BasePagination
        v-if="total > pageSize"
        :current-page="currentPage"
        :total-pages="totalPages"
        @page-change="updatePage"
      />
    </h2>
    <p role="status">
      <span
        v-if="!searchQuery.length && !departmentFilter"
        class="ui-font"
      >
        No search in progress.
      </span>
      <span
        v-else-if="isLoading"
        class="ui-font"
      >Search in progress</span>
      <span
        v-else-if="!courses.length"
        class="ui-font"
      >No results</span>
      <span
        v-else
        class="visually-hidden ui-font"
      >{{ courses.length }} results found</span>
    </p>
    <div
      v-if="total"
      class="courses-list__courses"
      data-test="table-container"
    >
      <BaseTable
        :rows="coursesWithDepartment"
        @select="goToCourse"
      >
        <template #header>
          <th class="th">Code</th>
          <th class="th">Name</th>
          <th class="th">Description</th>
          <th class="th">Department</th>
        </template>
        <template #default="{ row: course }">
          <td>
            <NuxtLink
              :to="getCourseUrl(course)"
              class="ui-font"
            >
              {{ course.code || course.subjectCode + course.courseNumber }}
            </NuxtLink>
          </td>
          <td>
            {{ courseDisplayHeading(course) }}
          </td>
          <td :title="course.description">
            {{ getShortDescription(course.description) || 'No Description Set' }}
          </td>
          <td>
            {{ course.department ? course.department.displayName : '-' }}
          </td>
        </template>
      </BaseTable>
    </div>
    <div class="max-w-screen-xl mx-auto flex">
      <div class="theme-page-content flex justify-center link-container">
        <a v-if="enablePrintList" data-test-id="print-link" class="link text-md" :href="printPath">
          Download Course List as PDF
        </a>
      </div>
    </div>
    <template v-if="chosenDepartments.length">
      <h2 class="text-xl font-normal mt-8 ">
        Browse Courses
      </h2>
      <div class="flex flex-wrap mt-4 -mx-2">
        <NuxtLink
          v-for="(department, index) of chosenDepartments"
          :key="index"
          class="md:w-1/3 px-2 ui-font"
          :to="`/departments/${encodeURIComponent(department.id)}/courses`"
        >
          <div class="card p-4 md:mb-4 font-semibold">
            {{ department.displayName }}
          </div>
        </NuxtLink>
      </div>
    </template>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import SearchInput from '@/components/SearchInput'
import DepartmentFilter from '@/components/DepartmentFilter'
import RichContent from '@/components/RichContent'
import useCourses from '@/composables/useCourses'
import { getShortDescription } from '@/utils/generalUtils'
import useInterpolation from '@/composables/useInterpolation'
import { generateCourseLabel } from '@/utils/dataLabels'
import useFeatureFlags from '@/composables/useFeatureFlags'
import { TITLE_INTERPOLATIONS, PRINT_LIST } from '@/constants/featureFlags'
import usePrint from '@/composables/usePrint'
import useMeta from '@/composables/useMeta'

export default {
  components: {
    SearchInput,
    DepartmentFilter,
    RichContent,
  },
  props: {
    pageContent: {
      type: String,
      default: null,
    },
  },
  setup(_, { root }) {
    const { enableTitleInterpolations, enablePrintList } = useFeatureFlags(
      {
        enableTitleInterpolations: TITLE_INTERPOLATIONS,
        enablePrintList: PRINT_LIST,
      }
    )

    const columns = [
      'displayName',
      'department',
      'description',
      'name',
      'courseNumber',
      'subjectCode',
      'code',
      'courseGroupId',
      'credits.creditHours',
      'career',
      ...(enableTitleInterpolations
        ? [
            'college',
            'longName',
            'status',
            'institution',
            'institutionId',
            'customFields.rawCourseId',
            'customFields.crseOfferNbr',
            'customFields.catalogAttributes',
          ]
        : []),
    ]
    const {
      updatePage,
      search,
      fetchData,
      coursesWithDepartment,
      hasDepartmentFilter,
      totalPages,
      departmentFilter,
      courses,
      filters,
      total,
      currentPage,
      isLoading,
      searchQuery,
      pageSize,
      goToCourse,
      getCourseUrl,
    } = useCourses(root, { columns })

    const { getInterpolatedFieldValue } = useInterpolation()

    const { printPath } = usePrint(root)

    const { catalogWideMetaTitle } = useMeta()

    return {
      updatePage,
      search,
      fetchData,
      coursesWithDepartment,
      hasDepartmentFilter,
      totalPages,
      departmentFilter,
      courses,
      filters,
      total,
      currentPage,
      isLoading,
      searchQuery,
      pageSize,
      goToCourse,
      getCourseUrl,
      getInterpolatedFieldValue,
      enableTitleInterpolations,
      enablePrintList,
      printPath,
      catalogWideMetaTitle,
    }
  },
  head() {
    return {
      title: `Courses | ${this.catalogWideMetaTitle}`,
      meta: [
        {
          hid: 'description',
          name: 'description',
          content: `Courses | ${this.catalogWideMetaTitle}`,
        },
      ],
    }
  },
  computed: {
    ...mapState('departments', {
      chosenDepartments: (state) => state.chosen || state.all,
    }),
    ...mapGetters('settings', ['layout']),
  },
  watch: {
    'filters.department': 'search',
    '$route.query': 'fetchData',
  },
  mounted() {
    this.fetchData({
      sortBy: 'code',
    })
  },
  methods: {
    getShortDescription,
    courseDisplayHeading(course) {
      return generateCourseLabel(course, undefined, {
        getInterpolatedFieldValue: this.enableTitleInterpolations
          ? this.getInterpolatedFieldValue
          : undefined,
        nameOnlyFallback: true,
      })
    },
  },
}
</script>

<style lang="css" scoped>
.search-wrapper /deep/ .card {
  display: flex !important;
  margin-bottom: 0;
}

@screen md {
  .courses-list {
    column-count: 3;
  }
}

.link-container {
  width: 240px;
  padding-top: 28px;
  padding-bottom: 28px;
}

.link {
  cursor: pointer;
  text-decoration: underline;
  color: var(--h2HeaderFontColor, var(--text-gray-900));
  font-family: var(--h2HeaderFontTypeface, inherit);
  font-weight: var(--h2HeaderFontWeight, 700);
}
</style>
